import { isAdminPermission } from '@carcheck/shared';

import type { AcceptInviteAdminDto, AdminUserDto, UserDto } from './types';

/**
 * Maps the server's `UserDto` (full user shape, used across the API) to
 * the admin-panel's `AdminUserDto`.
 *
 * Used post-`/auth/totp/challenge` (TOTP-required branch of the
 * password-login flow) — the legacy challenge endpoint still returns
 * the `AuthTokensDto` shape with an embedded `user`.
 *
 * Renames `phoneNumber → phone` and stubs `permissions` to an empty
 * array — the legacy endpoint doesn't carry the admin-scoped permission
 * list. `GET /admin/me` (post-AUT-110) will fill this in once it ships.
 */
export const toAdminUserDto = (user: UserDto): AdminUserDto => ({
  id: user.id,
  phone: user.phoneNumber,
  firstName: user.firstName,
  lastName: user.lastName,
  permissions: [],
});

/**
 * Map an `AcceptInviteAdminDto` (shape returned by `POST /admin/auth/login`
 * + `POST /admin/invites/accept` post-AUT-108) onto the SPA's
 * `AdminUserDto` shape.
 *
 * The wire shape is narrow (userId + role + permissions) — no phone,
 * no name. We fill phone/name as `null` and project the wire permission
 * array onto the SPA's `AdminPermission[]` via the canonical
 * `isAdminPermission` guard (NOT a hand-maintained subset).
 *
 * Why the guard, not a subset allow-list: a subset silently DROPS valid
 * permissions the SPA hasn't yet wired a feature for. That's how every
 * admin (incl. super_admin) lost `manage_users` + `view_pii` pre-AUT-124
 * — the users page failed closed (all PII redacted, Suspend hidden).
 * Filtering through `isAdminPermission` keeps every wire-valid permission
 * (the backend casts the full Prisma `AdminPermission[]` onto the wire),
 * dropping only genuinely-unknown strings. New permissions are carried
 * automatically.
 *
 * Once `GET /admin/me` ships the SPA will refresh from the canonical
 * endpoint and pick up `phone` / `firstName` / `lastName`.
 */
export const adminLoginAdminToAdminUserDto = (admin: AcceptInviteAdminDto): AdminUserDto => ({
  id: admin.userId,
  phone: null,
  firstName: null,
  lastName: null,
  permissions: admin.permissions.filter(isAdminPermission),
});
